<template>
    <div class="container py-4">
        <div class="mb-4">
            <h1 class="h3 mb-1">Cherry‑Pick Weather Trends</h1>
            <p class="text-muted mb-0">See how changing the start/end year can flip an apparent trend. Built for
                exploration, not persuasion.</p>
        </div>

        <div class="row g-3 align-items-end">
            <div class="col-md-4">
                <label class="form-label">Location (city or lat,lon)</label>
                <input v-model="location" class="form-control" placeholder="Austin, TX or 30.27,-97.74" />
            </div>
            <div class="col-md-3">
                <label class="form-label">Element</label>
                <select v-model="selectedElementKey" class="form-select">
                    <option v-for="opt in elementOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
                </select>
            </div>
            <div class="col-md-3">
                <label class="form-label">Aggregation</label>
                <select v-model="aggregation" class="form-select">
                    <option v-for="m in aggregationOptionsForSelected" :key="m" :value="m">{{ m }}</option>
                </select>
            </div>
            <div class="col-md-2">
                <label class="form-label">Min window (yrs)</label>
                <select v-model.number="minWindowYears" class="form-select">
                    <option :value="3">3</option>
                    <option :value="5">5</option>
                    <option :value="7">7</option>
                    <option :value="10">10</option>
                </select>
            </div>
            <div class="col-md-6">
                <label class="form-label">Visual Crossing API key</label>
                <input v-model="apiKeyLocal" class="form-control" placeholder="enter api key" />
            </div>
            <div class="col-md-3 d-grid">
                <button :disabled="loading || !canFetch" @click="fetchData" class="btn btn-primary">{{ loading ?
                    'Loading…' : 'Fetch data since 1995' }}</button>
            </div>
            <div class="col-md-3 d-grid">
                <button :disabled="!annualSeries.length" @click="exportCsv" class="btn btn-outline-secondary">Export
                    annual CSV</button>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger my-3">{{ error }}</div>

        <div class="row g-4 mt-1">
            <div class="col-lg-8">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <div>
                                <div class="small text-muted">Overall trend <span v-if="firstYear && lastYear">({{
                                        firstYear }}–{{ lastYear }})</span></div>
                                <div class="fw-semibold">slope: {{ formatNum(overallSlope) }} per year</div>
                            </div>
                            <div class="d-flex align-items-center gap-2">
                                <label class="small me-2 mb-0">Preview start year</label>
                                <select v-model.number="previewStartYear" class="form-select form-select-sm"
                                    style="width:auto">
                                    <option v-for="y in yearChoices" :key="y" :value="y">{{ y }}</option>
                                </select>
                                <span class="small ms-2" v-if="previewSlope !== null">slope from {{ previewStartYear }}:
                                    {{ formatNum(previewSlope) }}</span>
                            </div>
                        </div>

                        <!-- Chart always visible: shows placeholder axes if no data -->
                        <div class="position-relative" style="width:100%;">
                            <svg ref="chartRef" class="w-100" :style="{height: chartHeight+'px'}"
                                preserveAspectRatio="xMidYMid meet"></svg>
                            <div v-if="!annualSeries.length"
                                class="position-absolute top-50 start-50 translate-middle text-muted">
                                <div class="text-center">No data yet. Enter a location and fetch.</div>
                            </div>
                        </div>
                        <div class="form-text mt-2">Solid line shows annual aggregate; dashed line shows best‑fit trend.
                            Dots are per‑year values.</div>
                    </div>
                </div>
            </div>

            <div class="col-lg-4">
                <div class="card h-100">
                    <div class="card-body">
                        <h2 class="h6">Cherry‑pick ranges</h2>
                        <p class="small text-muted mb-2">Opposite‑sign start→end windows (≥ {{ minWindowYears }} yrs)
                            ranked by strongest flip.</p>

                        <!-- Fix: use proper v-if / v-else syntax -->
                        <div v-if="cherryAnyEnd.length">
                            <div class="table-responsive">
                                <table class="table table-sm table-striped align-middle">
                                    <thead>
                                        <tr>
                                            <th>start</th>
                                            <th>end</th>
                                            <th>yrs</th>
                                            <th>slope</th>
                                            <th>claim</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in cherryAnyEnd.slice(0, 10)" :key="row.start + '-' + row.end">
                                            <td>{{ row.start }}</td>
                                            <td>{{ row.end }}</td>
                                            <td>{{ row.years }}</td>
                                            <td>{{ formatNum(row.slope) }}</td>
                                            <td>{{ row.claim }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else>
                            <div class="alert alert-info">
                                No ranges flip the sign. Try these instead:
                                <ul class="mb-0 mt-2 small">
                                    <li><span class="fw-semibold">Near‑flip windows:</span> smallest‑magnitude slopes
                                        (closest to zero).</li>
                                    <li><span class="fw-semibold">Strongest supporting windows:</span> same‑sign but
                                        largest magnitude (most persuasive).</li>
                                </ul>
                            </div>


                            <div class="mt-3">
                                <h3 class="h6">Near‑flip windows</h3>
                                <div class="table-responsive">
                                    <table class="table table-sm table-striped align-middle">
                                        <thead>
                                            <tr>
                                                <th>start</th>
                                                <th>end</th>
                                                <th>yrs</th>
                                                <th>slope</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in nearFlip.slice(0, 10)" :key="row.start + '-' + row.end">
                                                <td>{{ row.start }}</td>
                                                <td>{{ row.end }}</td>
                                                <td>{{ row.years }}</td>
                                                <td>{{ formatNum(row.slope) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>


                                <h3 class="h6 mt-3">Strongest supporting windows</h3>
                                <div class="table-responsive">
                                    <table class="table table-sm table-striped align-middle">
                                        <thead>
                                            <tr>
                                                <th>start</th>
                                                <th>end</th>
                                                <th>yrs</th>
                                                <th>slope</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in strongestSupport.slice(0, 10)"
                                                :key="row.start + '-' + row.end">
                                                <td>{{ row.start }}</td>
                                                <td>{{ row.end }}</td>
                                                <td>{{ row.years }}</td>
                                                <td>{{ formatNum(row.slope) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <details class="mt-4">
            <summary class="mb-2">Show annual table</summary>
            <div class="table-responsive">
                <table class="table table-sm table-striped">
                    <thead>
                        <tr>
                            <th>year</th>
                            <th>value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="pt in annualSeries" :key="pt.year">
                            <td>{{ pt.year }}</td>
                            <td>{{ formatNum(pt.value) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </details>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import * as d3 from 'd3'

const props = defineProps<{ apiKey?: string }>()

const location = ref('Austin, TX')
const apiKeyLocal = ref(props.apiKey ?? '')
const selectedElementKey = ref<'tavg' | 'tmin' | 'tmax' | 'precip' | 'snow'>('tavg')
const aggregation = ref<'mean' | 'sum' | 'min' | 'max'>('mean')
const minWindowYears = ref(5)
const loading = ref(false)
const error = ref('')
const chartHeight = 360

const elementOptions = [
  { key: 'tavg', label: 'temperature (mean of daily avg)', defaultAgg: 'mean' },
  { key: 'tmin', label: 'temperature min (mean of daily min)', defaultAgg: 'mean' },
  { key: 'tmax', label: 'temperature max (mean of daily max)', defaultAgg: 'mean' },
  { key: 'precip', label: 'precipitation (sum of daily precip)', defaultAgg: 'sum' },
  { key: 'snow', label: 'snowfall (sum of daily snow)', defaultAgg: 'sum' }
] as const

type ElementKey = typeof elementOptions[number]['key']

const aggregationOptions: Record<ElementKey, Array<'mean' | 'sum' | 'min' | 'max'>> = {
  tavg: ['mean', 'min', 'max'],
  tmin: ['mean', 'min', 'max'],
  tmax: ['mean', 'min', 'max'],
  precip: ['sum', 'mean', 'max'],
  snow: ['sum', 'mean', 'max']
}

const aggregationOptionsForSelected = computed(() => aggregationOptions[selectedElementKey.value])

watch(selectedElementKey, () => {
  const def = elementOptions.find(e => e.key === selectedElementKey.value)
  aggregation.value = (def?.defaultAgg as any) ?? 'mean'
})

const canFetch = computed(() => location.value.trim().length > 0 && (apiKeyLocal.value?.trim()?.length ?? 0) > 0)

interface DayRow { datetime: string; temp?: number; tempmin?: number; tempmax?: number; precip?: number; snow?: number }
interface AnnualPoint { year: number; value: number }

const dailyRows = ref<DayRow[]>([])
const annualSeries = ref<AnnualPoint[]>([])

// --- Chart ---
const chartRef = ref<SVGSVGElement | null>(null)
let resizeObs: ResizeObserver | null = null

function drawChart() {
  const svgEl = chartRef.value
  if (!svgEl) return
  const svg = d3.select(svgEl)
  svg.selectAll('*').remove()

  const width = svgEl.clientWidth || 800
  const height = chartHeight

  const margin = { top: 16, right: 24, bottom: 34, left: 46 }
  const innerWidth = Math.max(100, width - margin.left - margin.right)
  const innerHeight = Math.max(80, height - margin.top - margin.bottom)

  const g = svg
    .attr('viewBox', `0 0 ${width} ${height}`)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  // placeholder axes if no data
  if (!annualSeries.value.length) {
    const x = d3.scaleLinear().domain([1995, 2005]).range([0, innerWidth])
    const y = d3.scaleLinear().domain([0, 1]).range([innerHeight, 0])
    g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(x).tickFormat(d3.format('d')))
    g.append('g').call(d3.axisLeft(y))
    return
  }

  const x = d3.scaleLinear()
    .domain(d3.extent(annualSeries.value, d => d.year) as [number, number])
    .range([0, innerWidth])

  const y = d3.scaleLinear()
    .domain(d3.extent(annualSeries.value, d => d.value) as [number, number])
    .nice()
    .range([innerHeight, 0])

  // gridlines
  g.append('g')
    .attr('class', 'grid')
    .attr('transform', `translate(0,${innerHeight})`)
    .call(d3.axisBottom(x).ticks(8).tickSize(-innerHeight).tickFormat(() => ''))
  g.append('g')
    .attr('class', 'grid')
    .call(d3.axisLeft(y).ticks(6).tickSize(-innerWidth).tickFormat(() => ''))

  // line & points
  const line = d3.line<AnnualPoint>().x(d => x(d.year)).y(d => y(d.value))
  g.append('path')
    .datum(annualSeries.value)
    .attr('fill', 'none')
    .attr('stroke', '#0d6efd')
    .attr('stroke-width', 2)
    .attr('d', line)

  g.selectAll('circle')
    .data(annualSeries.value)
    .enter()
    .append('circle')
    .attr('cx', d => x(d.year))
    .attr('cy', d => y(d.value))
    .attr('r', 2.5)
    .attr('fill', '#0d6efd')

  // overall trend line (dashed)
  const vals = annualSeries.value.map(p=>p.value)
  const reg = linearRegression(vals)
  const years = annualSeries.value.map(p=>p.year)
  const xIdx = d3.range(vals.length)
  const trendValues = xIdx.map(i => reg.intercept + reg.slope * i)
  const trendPts = years.map((yVal, i) => ({ year: yVal, value: trendValues[i] }))
  const trend = d3.line<AnnualPoint>().x(d => x(d.year)).y(d => y(d.value))
  g.append('path')
    .datum(trendPts)
    .attr('fill', 'none')
    .attr('stroke', '#6c757d')
    .attr('stroke-width', 2)
    .attr('stroke-dasharray', '6,6')
    .attr('d', trend)

  // axes
  g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(x).tickFormat(d3.format('d')))
  g.append('g').call(d3.axisLeft(y))
}

onMounted(() => {
  drawChart()
  if (chartRef.value && 'ResizeObserver' in window) {
    resizeObs = new ResizeObserver(() => drawChart())
    resizeObs.observe(chartRef.value)
  }
})

onBeforeUnmount(() => { resizeObs?.disconnect() })

// --- helpers & analytics ---
function yearOf(d: string) { return Number(d.slice(0, 4)) }
function formatNum(n: number | null) { return n === null ? '—' : Math.round(n * 1000) / 1000 }

function aggregateAnnual(rows: DayRow[], key: ElementKey, method: 'mean'|'sum'|'min'|'max'): AnnualPoint[] {
  const byYear = new Map<number, number[]>()
  const mapVal = (r: DayRow): number | null => {
    if (key === 'tavg') return r.temp ?? null
    if (key === 'tmin') return r.tempmin ?? null
    if (key === 'tmax') return r.tempmax ?? null
    if (key === 'precip') return r.precip ?? null
    if (key === 'snow') return r.snow ?? null
    return null
  }
  for (const r of rows) {
    const v = mapVal(r)
    if (v == null || !Number.isFinite(v)) continue
    const y = yearOf(r.datetime)
    if (!byYear.has(y)) byYear.set(y, [])
    byYear.get(y)!.push(v)
  }
  const years = Array.from(byYear.keys()).sort((a,b)=>a-b)
  const series: AnnualPoint[] = years.map(y => {
    const arr = byYear.get(y)!
    let val: number
    if (method === 'sum') val = arr.reduce((a,b)=>a+b,0)
    else if (method === 'min') val = Math.min(...arr)
    else if (method === 'max') val = Math.max(...arr)
    else val = arr.reduce((a,b)=>a+b,0)/arr.length
    return { year: y, value: val }
  })
  return series
}

function linearRegression(values: number[]) {
  const n = values.length
  const xmean = (n-1)/2
  const ymean = values.reduce((a,b)=>a+b,0)/n
  let num = 0, den = 0
  for (let i=0;i<n;i++) {
    const dx = i - xmean
    num += dx * (values[i] - ymean)
    den += dx * dx
  }
  const slope = den === 0 ? 0 : num / den
  const intercept = ymean - slope * xmean
  return { slope, intercept }
}

function slopeFromIndex(startIdx: number, values: number[]): number | null {
  if (startIdx < 0 || startIdx >= values.length - 1) return null
  const sub = values.slice(startIdx)
  return linearRegression(sub).slope
}

function slopeRange(i:number, j:number, values:number[]) {
  const sub = values.slice(i, j+1)
  return linearRegression(sub).slope
}

const firstYear = computed(() => annualSeries.value.length ? annualSeries.value[0].year : null)
const lastYear = computed(() => annualSeries.value.length ? annualSeries.value[annualSeries.value.length-1].year : null)
const overallSlope = computed(() => {
  if (!annualSeries.value.length) return 0
  return linearRegression(annualSeries.value.map(p=>p.value)).slope
})

const yearChoices = computed(() => annualSeries.value.slice(0, -1).map(p=>p.year))
const previewStartYear = ref<number | null>(null)
const previewSlope = ref<number | null>(null)

watch(previewStartYear, () => {
  if (previewStartYear.value == null) { previewSlope.value = null; return }
  const idx = annualSeries.value.findIndex(p=>p.year === previewStartYear.value)
  previewSlope.value = slopeFromIndex(idx, annualSeries.value.map(p=>p.value))
})

// Opposite‑sign cherry picks allowing ANY end year (>= minWindowYears)
const cherryAnyEnd = computed(() => {
  const N = annualSeries.value.length
  if (N < 3) return [] as { start:number,end:number,years:number,slope:number,claim:string }[]
  const vals = annualSeries.value.map(p=>p.value)
  const years = annualSeries.value.map(p=>p.year)
  const overall = overallSlope.value
  const out: { start:number,end:number,years:number,slope:number,claim:string }[] = []
  for (let i=0;i<N-1;i++) {
    for (let j=i+1;j<N;j++) {
      const yrs = years[j] - years[i] + 1
      if (yrs < minWindowYears.value) continue
      const s = slopeRange(i,j,vals)
      if ((overall > 0 && s < 0) || (overall < 0 && s > 0)) {
        out.push({ start: years[i], end: years[j], years: yrs, slope: s, claim: overall > 0 ? 'decreasing' : 'increasing' })
      }
    }
  }
  out.sort((a,b)=>(b.end===lastYear.value ? 10:1 )* Math.abs(b.slope) - Math.abs(a.slope))
  return out
})

// If no flips: suggest near‑flip (|slope| minimal) and strongest supporting (same sign, |slope| max)
const nearFlip = computed(() => {
  const N = annualSeries.value.length
  if (N < 3) return [] as { start:number,end:number,years:number,slope:number }[]
  const vals = annualSeries.value.map(p=>p.value)
  const years = annualSeries.value.map(p=>p.year)
  const overall = overallSlope.value
  const out: { start:number,end:number,years:number,slope:number }[] = []
  for (let i=0;i<N-1;i++) {
    for (let j=i+1;j<N;j++) {
      const yrs = years[j] - years[i] + 1
      if (yrs < minWindowYears.value) continue
      const s = slopeRange(i,j,vals)
      // same sign as overall but closest to zero
      if ((overall >= 0 && s >= 0) || (overall <= 0 && s <= 0)) {
        out.push({ start: years[i], end: years[j], years: yrs, slope: s })
      }
    }
  }
  out.sort((a,b)=>Math.abs(a.slope) - Math.abs(b.slope))
  return out
})

const strongestSupport = computed(() => {
  const N = annualSeries.value.length
  if (N < 3) return [] as { start:number,end:number,years:number,slope:number }[]
  const vals = annualSeries.value.map(p=>p.value)
  const years = annualSeries.value.map(p=>p.year)
  const overall = overallSlope.value
  const out: { start:number,end:number,years:number,slope:number }[] = []
  for (let i=0;i<N-1;i++) {
    for (let j=i+1;j<N;j++) {
      const yrs = years[j] - years[i] + 1
      if (yrs < minWindowYears.value) continue
      const s = slopeRange(i,j,vals)
      if ((overall >= 0 && s >= 0) || (overall <= 0 && s <= 0)) {
        out.push({ start: years[i], end: years[j], years: yrs, slope: s })
      }
    }
  }
  out.sort((a,b)=>Math.abs(b.slope) - Math.abs(a.slope))
  return out
})

async function fetchData() {
  error.value = ''
  if (!canFetch.value) { error.value = 'enter location and api key'; return }
  loading.value = true
  try {
    const today = new Date()
    //const y = today.getFullYear(), m = String(today.getMonth()+1).padStart(2,'0'), d = String(today.getDate()).padStart(2,'0')
    //const end = `${y}-${m}-${d}`
    const y = today.getFullYear()-1;
    const end = `${y}-12-31`
    const fields = ['datetime','temp','tempmin','tempmax','precip','snow']
    const url = new URL(`https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(location.value)}/1995-01-01/${end}`)
    url.searchParams.set('unitGroup','metric')
    url.searchParams.set('include','days')
    url.searchParams.set('elements', fields.join(','))
    url.searchParams.set('key', apiKeyLocal.value.trim())
    url.searchParams.set('contentType','json')

    const res = await fetch(url.toString())
    if (!res.ok) throw new Error(`api error ${res.status}`)
    const json = await res.json()
    const rows: DayRow[] = (json.days || []).map((d:any)=>({
      datetime: d.datetime,
      temp: isFiniteNumber(d.temp) ? Number(d.temp) : undefined,
      tempmin: isFiniteNumber(d.tempmin) ? Number(d.tempmin) : undefined,
      tempmax: isFiniteNumber(d.tempmax) ? Number(d.tempmax) : undefined,
      precip: isFiniteNumber(d.precip) ? Number(d.precip) : undefined,
      snow: isFiniteNumber(d.snow) ? Number(d.snow) : undefined
    }))
    dailyRows.value = rows
    recompute()
  } catch (e:any) {
    error.value = String(e?.message || e)
  } finally {
    loading.value = false
  }
}

function isFiniteNumber(v:any) { return typeof v === 'number' && Number.isFinite(v) }

function recompute() {
  annualSeries.value = aggregateAnnual(dailyRows.value, selectedElementKey.value, aggregation.value)
  previewStartYear.value = firstYear.value
  drawChart()
}

watch([selectedElementKey, aggregation, minWindowYears], () => { if (dailyRows.value.length) recompute() })

function exportCsv() {
  const header = 'year,value\n'
  const body = annualSeries.value.map(r=>`${r.year},${r.value}`).join('\n')
  const blob = new Blob([header+body], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `annual_${selectedElementKey.value}_${aggregation.value}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(() => { annualSeries.value = []; drawChart() })
</script>

<style scoped>
.grid line { stroke: rgba(0,0,0,0.06); }
</style>
